import { useAuthStore } from '@/auth/authStore';
import { SERVER_URL, COMMON_HEADERS } from '@/config/serverUrl';
import type { FrameSpec, RankTier } from '@shared/types';

const BASE = SERVER_URL;

export type LeaderboardTab = 'mmr' | 'time' | 'tiers';
export type LeaderboardMode = 'solo' | 'duo';

export interface LeaderboardRow {
  id: string;
  displayName: string;
  avatarUrl: string | null;
  avatarColor: 'red' | 'blue';
  rank: RankTier;
  rankDivision: number;
  rankStars: number;
  rankDuo: RankTier;
  rankDivisionDuo: number;
  rankStarsDuo: number;
  mmr: number;
  mmrSolo: number;
  mmrDuo: number;
  matchesPlayed: number;
  matchesWon: number;
  bestTimeMs: number | null;
  characterIdleUrl?: string | null;
  frame?: FrameSpec | null;
}

export async function fetchLeaderboard(
  tab: Exclude<LeaderboardTab, 'tiers'>,
  mode: LeaderboardMode = 'solo',
  limit = 50,
): Promise<LeaderboardRow[]> {
  const token = useAuthStore.getState().token;
  const res = await fetch(`${BASE}/leaderboard/?tab=${tab}&mode=${mode}&limit=${limit}`, {
    headers: { ...COMMON_HEADERS, ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    credentials: 'include',
  });
  if (!res.ok) {
    throw new Error(`Leaderboard ${res.status}`);
  }
  const body = (await res.json()) as { items: LeaderboardRow[] };
  return body.items;
}

export async function fetchLeaderboardByTier(
  mode: LeaderboardMode = 'solo',
): Promise<Partial<Record<RankTier, LeaderboardRow[]>>> {
  const token = useAuthStore.getState().token;
  const res = await fetch(`${BASE}/leaderboard/tiers?mode=${mode}`, {
    headers: { ...COMMON_HEADERS, ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    credentials: 'include',
  });
  if (!res.ok) {
    throw new Error(`Leaderboard ${res.status}`);
  }
  const body = (await res.json()) as {
    byTier: Partial<Record<RankTier, LeaderboardRow[]>>;
  };
  return body.byTier;
}

/** Format ms → "9.42s" (under a minute) or "1:23.4" (>=1min). */
export function formatTime(ms: number | null): string {
  if (ms == null) return '—';
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(2)}s`;
  const m = Math.floor(s / 60);
  const rem = (s - m * 60).toFixed(1);
  return `${m}:${rem.padStart(4, '0')}`;
}
